import type {
  ICollision,
  CollideHandler,
} from '../../actions/collisions/ICollision';
import { InvalidEffect } from '../../actions/effects/InvalidEffect';
import type { CollideEvent, CollideId } from '../../defs/collide';
import {
  despawnBlockEntity,
  spawnBlockEntity,
} from '../../entities/BlockEntity';
import type { RainbowBubbleBlockEntity } from '../../entities/BlockEntity';
import type { GameScene } from '../../GameScene';
import type { RainbowSoapBlockEntity } from './RainbowSoapBlockEntity';
import { Vector2 } from '../../../odie';
import { RainbowSoapBlockEffect } from './RainbowSoapBlockEffect';
import { waitAFrame } from '../../../lib/util/waitAFrame';
import { canSpawnBubbleOnCell } from '../../util/bubbleTools';

export class RainbowSoapBlockCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: RainbowSoapBlockEntity;
  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    block: this.resolveBlock,
    attack: this.resolveAttack,
  };

  constructor(scene: GameScene, entity: RainbowSoapBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap() {
    await new InvalidEffect(this.entity).execute();
    return false;
  }

  private async resolveBlock(event: CollideEvent) {
    this.attack();
    return true;
  }

  private async resolveAttack(event: CollideEvent) {
    this.attack();
    return true;
  }

  private async attack() {
    const entity = this.entity;
    const effect = new RainbowSoapBlockEffect(this.scene);
    effect.animateBreak(entity);
    effect.animateBreakParticles(entity);
    despawnBlockEntity(this.scene, entity);
    // Wait a frame so the game can process the new puzzle state
    await waitAFrame();
    await this.spawnRainbowBubbles();
  }

  private async spawnRainbowBubbles() {
    // Find out valid surrounding positions to spawn rainbow bubbles
    const map = this.scene.sessionEntity.c.map;
    const position = this.entity.c.position.mapPosition;

    // 3x3 area relative to the original block position
    const targets = [
      { x: -1, y: -1 },
      { x: 0, y: -1 },
      { x: 1, y: -1 },
      { x: -1, y: 0 },
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: -1, y: 1 },
      { x: 0, y: 1 },
      { x: 1, y: 1 },
    ];

    const promises: Promise<void>[] = [];

    for (const target of targets) {
      const x = position.x + target.x;
      const y = position.y + target.y;

      // Check if the position is on the map
      if (!map.isPositionOnMap({ x, y })) continue;

      const cell = map.getCellAt({ x, y });
      if (!canSpawnBubbleOnCell(cell)) continue;

      const delay = Math.random() * 0.3;
      const promise = this.spawnRainbowBubble(
        position,
        new Vector2(x, y),
        delay,
      );
      promises.push(promise);
    }

    await Promise.all(promises);
  }
  private async spawnRainbowBubble(from: Vector2, to: Vector2, delay: number) {
    const spawnId = 'rainbowBubble';

    // Spawn the bubble entity
    const bubble = spawnBlockEntity(
      this.scene,
      { id: spawnId },
      to,
    ) as RainbowBubbleBlockEntity;

    // Increase the goal count for the rainbow bubble goal
    const count = this.scene.getGoalCount(spawnId);
    this.scene.setGoalCount(spawnId, count + 1);
    this.scene.events.publish({ id: 'ungoal', goalId: spawnId });

    // Animate the bubble spawn
    await new RainbowSoapBlockEffect(this.scene).animateSpawnedBubble(
      bubble,
      from,
      delay,
    );
  }
}
